import React, { Component } from 'react';
import HistoryItem from './HistoryItem';
import 'url-search-params-polyfill';
import withWorkflow from '../components/withWorkflow';
import Breadcrumb from '../components/Breadcrumb';
import NotificationBox, { BadgeCount } from '../components/NotificationBox';
import { getActiveStars, getAdvancedOrderSearchResults } from '../util/apiUtils';
import AdvancedPatientSearch from './AdvancedPatientSearch';
import { OrderStatuses, getOrderStatusText } from './OrderStatus';

/**
 * Represents the list of practitioner order history.
 */
class HistoryList extends Component {
	
	/**
	 * Constructor sets up the state and binds the functions.
	 */
	constructor(props) {
		super(props);
		this.state = {
			gsdlNo: '',
			days: null,
			status: Object.keys(OrderStatuses)[0],
			type: Object.keys(this.types)[0],
			selectedOrderId: null,
			message: null,
			messageClassName: null,
			allOrders: [],
			filteredOrders: [],
			initialized: false,
			activeStars: [],
			lastSearchQuery: null,
			filteredStatuses: new Set()
		}

		this.getOrderType = this.getOrderType.bind(this);
		this.setGsdlNo = this.setGsdlNo.bind(this);
		this.setDays = this.setDays.bind(this);
		this.setStatus = this.setStatus.bind(this);
		this.setType = this.setType.bind(this);
		this.setMessage = this.setMessage.bind(this);
		this.findOrders = this.findOrders.bind(this);
		this.filterOrders = this.filterOrders.bind(this);
		this.removeRequisitionResponseHandler = this.removeRequisitionResponseHandler.bind(this);
	}

	/**
	 * Set the step (for breadcrumb) and find the orders when the component
	 * mounts.
	 */
	componentDidMount() {
		this.loadActiveStars();
		this.props.setStep("order-list");
		let params = new URLSearchParams(this.props.history.location.search);

		this.setState((state, props) => ({
			days: params.get("days") || "30",
			status: params.get("status") || state.status,
			type: params.get("type") || state.type,
			selectedOrderId: params.get("orderNumber") || state.selectedOrderId
		}));
	}

	/**
	 * Updates the history whenever the filter (days/status) changes.
	 */
	componentDidUpdate(prevProps, prevState, snapshot) {
		if (
			!this.state.skipURLUpdate && 
			(this.state.days !== prevState.days ||
			this.state.status !== prevState.status ||
			this.state.type !== prevState.type)
		) {
			const newSearchString = '?days=' +
				this.state.days +
				'&status=' +
				this.state.status +
				'&type=' +
				this.state.type +
				(this.state.selectedOrderId ? '&orderNumber=' + this.state.selectedOrderId : '');
			
			if (!prevProps.location?.search || prevProps.location.search !== newSearchString) {
				this.props.history.push({
					search: newSearchString
				});
			}

		} else if (this.props.location?.search && prevProps.location?.search && this.props.location.search !== prevProps.location?.search) {
			// Update state and apply filtering if the URL params change.
			let params = new URLSearchParams(this.props.history.location.search);

			this.setState((state, props) => ({
				days: params.get("days") || "14",
				status: params.get("status") || state.status,
				type: params.get("type") || state.type,
				selectedOrderId: params.get("orderNumber") || state.selectedOrderId
			}), this.filterOrders);
		}
	}

	/**
	 * The history days filter options.
	 */
	days = {
		"ANY": "Any",
		"0": "Today",
		"1": "Yesterday",
		"7": "Past week",
		"30": "Past month",
		"365": "Past year"
	}

	/**
	 * The history order type filter options. Also references to display the
	 * status text on a history list item.
	 */
	types = {
		"ANY": "Any",
		"OFFICE": "Ship to Office", // the order was shipped to the practitioner's office
		"DROPSHIP": "Ship to Patient", // the order was shipped directly to the
										// patient
		"REGISTRATION": "Online Registration" // the order was handed off, but
												// the patient confirmation step
												// was not completed
	};

	/**
	 * Gets the type text for an order.
	 */
	getOrderType(order) {
		let orderType = this.types.REGISTRATION;
		
		let dropShipItems = order.requisitions
			.filter(req => req.patientDropship);
		let officeItems = order.requisitions
			.filter(req => req.shipToOffice);
		if (order.physicianInTheLoop) {
			// Do not include this type in the types list because we don't want it to show for all clients
			orderType = "Genova Connect";
		} else if (dropShipItems.length > 0) {
			orderType = this.types.DROPSHIP;
		} else if (officeItems.length > 0) {
			orderType = this.types.OFFICE;
		}

		return orderType;
	}
	/**
	 * Sets the practitioner GSDLNO filter.
	 */
	setGsdlNo(e) {
		this.setState({
			gsdlNo: e.currentTarget.value
		});
	}

	/**
	 * Sets the days filter option, finds the orders, and applies the current
	 * status filter.
	 */
	setDays(e) {
		e.preventDefault();

		/* 
		 * skipURLUpdate is true when the days value is updated from the AdvancedPatientSearch.
		 * This prevents the browser's queryString from changing. Otherwise, the queryString would 
		 * most likely change to days=ANY and refreshing the browser would result in a large 'ANY' search.
		 */
		this.setState({
			days: e.currentTarget.dataset.days,
			skipURLUpdate: !!e.currentTarget.dataset.skipURLUpdate
		});
	}

	/**
	 * Sets the status filter option and applies the status filter.
	 */
	setStatus(e) {
		e.preventDefault();
		this.setState({
			status: e.currentTarget.dataset.status
		}, () => {
			/* Normally changing a status will update the URL parameters and that will trigger a filter.
			 * The URL does not change when skipURLUpdate is true (Advanced Search was used). In that case
			 * we need to manually trigger the filter.
			 */
			if (this.state.skipURLUpdate) {
				this.filterOrders();
			}
		});
	}
	
	/**
	 * Sets the type filter option and applies the type filter.
	 */
	setType(e) {
		e.preventDefault();
		this.setState({
			type: e.currentTarget.dataset.type
		}, ()  => this.filterOrders());
	}


	/**
	 * Sets the message to display.
	 */
	setMessage(message, className) {
		this.setState({
			message: message,
			messageClassName: className
		});
	}

	/**
	 * Finds orders filtered by the supplied orderSearchForm.
	 * Status and OrderType filters are re-applied once the requisitions are found.
	 */
	findOrders(orderSearchForm) {
		// Do not execute the request if this exact query has already been fetched
		if (this.state.lastSearchQuery === JSON.stringify(orderSearchForm)) {
			return;
		}

		this.setState({ lastSearchQuery: JSON.stringify(orderSearchForm) });
		this.props.setLoading(true);
		getAdvancedOrderSearchResults(orderSearchForm)
			.then((result) => {
				this.updateAndFilterOrders(result);
			})
			.catch((error) => {
				this.setState({
					message: 'We\'re sorry, an unexpected error occurred while loading your order history.'
				});
				this.updateAndFilterOrders([]);
			})
			.finally(() => {
				this.props.setLoading(false);
			});
	}

	updateAndFilterOrders = (orders) => {
		// Build a status filter that only includes statuses present in the search result
		const statusFilter = new Set([Object.keys(OrderStatuses)[0]]);
		orders.forEach(order => statusFilter.add(Object.keys(OrderStatuses).find(key => OrderStatuses[key] === getOrderStatusText(order))));
		this.setState({
			allOrders: orders,
			filteredOrders: orders,
			initialized: true,
			filteredStatuses: statusFilter
		}, () => this.filterOrders());
	}

	/**
	 * Fetches the open STAR issues for this practitioner.
	 */
	loadActiveStars = () => {
		getActiveStars()
			.then((stars) => {
				this.setState({
					// We only need the order numbers. Map the array of objects into an array of order numbers.
					activeStars: stars.filter((s) => s.orderNo).map((s) => s.orderNo)
				});
			})
			.catch((err) => {
				// Ignore error. STARs notification will not be displayed
			});
	};

	/**
	 * Filters the list of orders.
	 */
	filterOrders() {
		this.props.setLoading(true);
		this.setState({
			filteredOrders: this.state.allOrders.filter(order => {
				return ((OrderStatuses[this.state.status] === OrderStatuses.ANY || getOrderStatusText(order) === OrderStatuses[this.state.status]) && (this.types[this.state.type] === this.types.ANY || this.getOrderType(order) === this.types[this.state.type]));
			})
		}, () => this.props.setLoading(false));
	}
	
	/**
	 * Handles the response from removing a requisition from an order.
	 * 
	 * @param gearReqId
	 *            the gear requisition id
	 * @param gearOrderId
	 *            the gear order id
	 */
	removeRequisitionResponseHandler(gearReqId, gearOrderId) {
		let newState = Object.assign({}, this.state);
		let allOrdersMatch = newState.allOrders.find(order => order.gearOrderId === gearOrderId);
		let filteredOrdersMatch = newState.filteredOrders.find(order => order.gearOrderId === gearOrderId);
		allOrdersMatch.requisitions = allOrdersMatch.requisitions.filter(req => req.gearRequisitionId !== gearReqId);
		filteredOrdersMatch.requisitions = filteredOrdersMatch.requisitions.filter(req => req.gearRequisitionId !== gearReqId);
		this.setState({
			allOrders: newState.allOrders,
			filteredOrders: newState.filteredOrders
		});
	}

	/**
	 * Renders the component.
	 */
	render() {
		// show an error message if things go south...
		let message = null;
		if (this.state.message) {
			message = (
				<div className={"alert " + (this.state.messageClassName ? this.state.messageClassName : "alert-danger")} role="alert">
					{this.state.message}
				</div>
			);
		}

		// show a message if no orders were found matching the filter
		let noOrdersFound = null;
		if (this.state.initialized && !this.state.filteredOrders.length) {
			noOrdersFound = <em>No orders found</em>;
		}

		const statusOptions = Array.from(this.state.filteredStatuses).map((statusText) => (
			<li key={statusText} role="presentation">
				<button
					type="button"
					role="menuitem"
					className="btn btn-link btn-filter"
					style={this.state.status === statusText ? { fontWeight: 'bold' } : {}}
					data-status={statusText}
					onClick={this.setStatus}
				>
					{OrderStatuses[statusText]}
				</button>
			</li>
		));

		// the rows of history entries
		const rows = this.state.filteredOrders
			.map((order, index) => (
			<HistoryItem
				order={order}
				getOrderType={this.getOrderType}
				types={this.types}
				isStepValidForUser={this.props.isStepValidForUser}
				index={index}
				setMessage={this.setMessage}
				findOrders={this.findOrders}
				userInfo={this.props.userInfo}
				setLoading={this.props.setLoading}
				selectedOrderId={this.state.selectedOrderId}
				removeRequisitionResponseHandler={this.removeRequisitionResponseHandler}
				key={order.gearOrderId + '_' + order.orderFormId}
				activeStars={this.state.activeStars}
			/>
		));

		return (
			<div>
				<Breadcrumb breadcrumbStep="Order History" singleLevel={true} />
				<div className="container">
					<div className="row">
						<div className="col-xs-8 col-sm-9 col-lg-10">
							<h1>Order History</h1>
							{message}
							<p>Online orders for your account</p>
						</div>
						<div className="col-xs-4 col-sm-3 col-lg-2">
							{!!this.state.activeStars.length && (
								<NotificationBox
									url="/mygdx/stars"
									tooltip="STAR items need your IMMEDIATE ATTENTION to reduce order delays."
									icon={<i className="fa fa-star text-danger"></i>}
									badge={<BadgeCount count={this.state.activeStars.length} color="red" />}
								>
									<p>
										STARs <small>(Specimen Triage and Recovery)</small>
									</p>
								</NotificationBox>
							)}
						</div>
					</div>
					<div>
						<div>
							<div className="row">
								<div className="col-xs-12">
									{this.state.days && (
										<AdvancedPatientSearch
											userInfo={this.props.userInfo}
											daysBack={this.state.days}
											findOrders={this.findOrders}
											updateDays={this.setDays}
										/>
									)}
								</div>
							</div>
							<table className="table table-condensed">
								<colgroup>
									<col style={{ width: '75px' }} />
									<col />
									<col />
									<col />
									<col style={{ width: '225px' }} />
									<col />
								</colgroup>
								<thead style={{ border: '2px solid #2c5990' }}>
									<tr>
										<th>GDX ID</th>
										<th>Date</th>
										<th>Patient</th>
										<th>Requisitions</th>
										<th>
											{/* A status filter size < 3 means we have 'ANY' and possibly one other status. No need to filter. */}
											{this.state.filteredStatuses.size < 3 ? (
												'Status'
											) : (
												<div className="dropdown cursor-pointer">
													<div className="dropdown-toggle" type="button" id="statusDropDownMenu" data-toggle="dropdown">
														Status
														<span className="caret"></span>
													</div>
													<ul className="dropdown-menu" role="menu" aria-labelledby="statusDropDownMenu">
														{statusOptions}
													</ul>
												</div>
											)}
										</th>
										<th>Options</th>
									</tr>
								</thead>
								{rows}
							</table>
							{noOrdersFound}
						</div>
					</div>
				</div>
			</div>
		);
	}
}
export default withWorkflow(HistoryList);